import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../Shared/Services/api.service';
import { endpoints } from '../../../../environments/endpoints';
import { MAX_PAGE_SIZE, FIRST_PAGE } from '../../../Consts/backoffice.const';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';
import { TypedResourceRequest } from '../../Shared/Interfaces/api-request.interface';
import { PlanListResponse } from '../../../Interfaces/api/backoffice/plan.interface';
import {
  SubscriptionCancelPayload,
  SubscriptionChangePayload,
  SubscriptionOverrideListResponse,
  SubscriptionOverridePayload,
  SubscriptionOverrideShowResponse,
  SubscriptionPreviewEnvelope,
  SubscriptionShowResponse,
  SubscriptionTrialPayload,
} from '../../../Interfaces/api/backoffice/subscription.interface';
import { OrganizationSubscription } from '../../../Models/backoffice/organization-subscription.model';
import { PlanSummary } from '../../../Models/backoffice/plan-summary.model';
import { SubscriptionOverride } from '../../../Models/backoffice/subscription-override.model';
import { SubscriptionPreview } from '../../../Models/backoffice/subscription-preview.model';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionControlService {
  private readonly _apiService = inject(ApiService);

  showRequest(
    organizationId: string,
  ): TypedResourceRequest<LaravelDefaultResponse<SubscriptionShowResponse>> {
    return this._apiService.get<LaravelDefaultResponse<SubscriptionShowResponse>>(
      endpoints.backoffice.organizationSubscription(organizationId),
      { skipLoading: true, skipError: true },
    );
  }

  plansRequest(): TypedResourceRequest<LaravelDefaultResponse<PlanListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<PlanListResponse>>(
      endpoints.backoffice.plans,
      { params: { page: FIRST_PAGE, pageSize: MAX_PAGE_SIZE }, skipLoading: true, skipError: true },
    );
  }

  overridesRequest(
    organizationId: string,
    itemId: string,
  ): TypedResourceRequest<LaravelDefaultResponse<SubscriptionOverrideListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<SubscriptionOverrideListResponse>>(
      endpoints.backoffice.organizationSubscriptionItemOverrides(organizationId, itemId),
      { skipLoading: true, skipError: true },
    );
  }

  preview(organizationId: string, payload: SubscriptionChangePayload): Observable<SubscriptionPreview> {
    const request = this._apiService.post<LaravelDefaultResponse<SubscriptionPreviewEnvelope>>(
      endpoints.backoffice.organizationSubscriptionPreview(organizationId),
      { body: { plan_id: payload.plan_id, offer_type: payload.offer_type }, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<SubscriptionPreviewEnvelope>>(request)
      .pipe(map((response) => new SubscriptionPreview(response.data.preview)));
  }

  update(
    organizationId: string,
    payload: SubscriptionChangePayload,
  ): Observable<OrganizationSubscription> {
    return this._mutate(
      endpoints.backoffice.organizationSubscriptionUpdate(organizationId),
      payload as unknown as Record<string, unknown>,
    );
  }

  pause(organizationId: string, reason: string): Observable<OrganizationSubscription> {
    return this._mutate(endpoints.backoffice.organizationSubscriptionPause(organizationId), {
      reason,
    });
  }

  resume(organizationId: string, reason: string): Observable<OrganizationSubscription> {
    return this._mutate(endpoints.backoffice.organizationSubscriptionResume(organizationId), {
      reason,
    });
  }

  cancel(
    organizationId: string,
    payload: SubscriptionCancelPayload,
  ): Observable<OrganizationSubscription> {
    return this._mutate(
      endpoints.backoffice.organizationSubscriptionCancel(organizationId),
      payload as unknown as Record<string, unknown>,
    );
  }

  extendTrial(
    organizationId: string,
    payload: SubscriptionTrialPayload,
  ): Observable<OrganizationSubscription> {
    return this._mutate(
      endpoints.backoffice.organizationSubscriptionTrial(organizationId),
      payload as unknown as Record<string, unknown>,
    );
  }

  applyOverride(
    organizationId: string,
    itemId: string,
    payload: SubscriptionOverridePayload,
  ): Observable<SubscriptionOverride> {
    const request = this._apiService.post<LaravelDefaultResponse<SubscriptionOverrideShowResponse>>(
      endpoints.backoffice.organizationSubscriptionItemOverrides(organizationId, itemId),
      { body: payload, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<SubscriptionOverrideShowResponse>>(request)
      .pipe(map((response) => new SubscriptionOverride(response.data.override)));
  }

  revertOverride(
    organizationId: string,
    itemId: string,
    overrideId: string,
    reason: string,
  ): Observable<SubscriptionOverride> {
    const request = this._apiService.post<LaravelDefaultResponse<SubscriptionOverrideShowResponse>>(
      endpoints.backoffice.organizationSubscriptionOverrideRevert(organizationId, itemId, overrideId),
      { body: { reason }, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<SubscriptionOverrideShowResponse>>(request)
      .pipe(map((response) => new SubscriptionOverride(response.data.override)));
  }

  toSubscription(
    response: LaravelDefaultResponse<SubscriptionShowResponse> | undefined,
  ): OrganizationSubscription | null {
    const row = response?.data?.subscription;

    return row ? new OrganizationSubscription(row) : null;
  }

  toPlans(response: LaravelDefaultResponse<PlanListResponse> | undefined): PlanSummary[] {
    return (response?.data?.plans ?? []).map((row) => new PlanSummary(row));
  }

  toOverrides(
    response: LaravelDefaultResponse<SubscriptionOverrideListResponse> | undefined,
  ): SubscriptionOverride[] {
    return (response?.data?.overrides ?? []).map((row) => new SubscriptionOverride(row));
  }

  private _mutate(
    resource: string,
    body: Record<string, unknown>,
  ): Observable<OrganizationSubscription> {
    const request = this._apiService.post<LaravelDefaultResponse<SubscriptionShowResponse>>(
      resource,
      { body, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<SubscriptionShowResponse>>(request)
      .pipe(map((response) => new OrganizationSubscription(response.data.subscription)));
  }
}
